import { Router } from "@angular/router";
import {
	HttpClient,
	HttpHeaders,
	HttpParams,
	HttpResponse,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
	BehaviorSubject,
	Observable,
	catchError,
	map,
	of,
	switchMap,
	tap,
	throwError,
} from "rxjs";

import { environment } from "src/environments/environment";
import { baseUrl } from "./global.service";
import { filterActiveBanks, normalizeApiListResponse } from "../../shared/utils/api-list.util";

export type UserNotificationType =
	| 'new_task'
	| 'new_message'
	| 'file'
	| 'mention'
	| 'merchant_reply'
	| 'task_closed'
	| 'task_reopened'
	| 'postponement_expired'
	| 'task_assigned'
	| 'participant_added'
	| 'broadcast'
	| 'expense_analysis_complete'
	| 'subscription_renewal_failed'
	| 'subscription_payment'
	| 'office_permission'
	| 'accountant_access_granted'
	| 'closed_month'
	| 'custom_payment'
	| 'other'
	| string;

export type NotificationSource =
	| 'system'
	| 'accountant'
	| 'employee'
	| 'admin'
	| 'customer';

export interface UserNotificationItem {
	id: number;
	title: string;
	message: string;
	url: string | null;
	is_read: boolean;
	read_at?: string | null;
	notification_type?: UserNotificationType | null;
	notification_source?: NotificationSource | null;
	recipient_user_id?: number;
	actor_id?: number | null;
	task_id?: number | null;
	message_id?: number | null;
	push_tag?: string | null;
	created_at: string;
}

export type NotificationInboxFilters = {
	isRead?: boolean;
	notificationType?: string;
	notificationSource?: string;
};

export type AdminBroadcastCreateModel = {
	title: string;
	message: string;
	audience: string;
	dispatchImmediately?: boolean;
	scheduledSendAt?: string | null;
	/** When false with immediate send: do not persist / exclude from admin list (backend). */
	persistHistory?: boolean;
};

export type BroadcastDeliveryStatus = 'NO_TOKEN' | 'SENT' | 'DELIVERED' | 'FAILED';

export interface BroadcastDeliveryReportRow {
	id: number;
	broadcast_notification_id: number;
	user_id: number;
	merchant_id: number | null;
	merchant_name_en: string | null;
	merchant_name_ar: string | null;
	merchant_name_he: string | null;
	merchant_id_number: string | null;
	user_name: string | null;
	phone_number: string | null;
	device_id: string | null;
	device_type: string | null;
	channel: 'fcm' | 'webpush' | null;
	has_token: boolean;
	delivery_status: BroadcastDeliveryStatus;
	error_code: string | null;
	error_message: string | null;
	attempted_at: string;
}

export interface BroadcastDeliveryReportSummary {
	targeted_users: number;
	distinct_merchants: number;
	registered_tokens: number;
	delivered_count: number;
	failed_count: number;
	no_token_users: number;
}

export interface BroadcastDeliveryReportResponse {
	broadcast: {
		broadcast_notification_id: number;
		title: string;
		message: string;
		audience: string;
		status: string;
		sent_at: string | null;
		recipient_user_count: number;
		fcm_token_count: number;
		fcm_success_count: number;
		fcm_failure_summary: string | null;
		error_message: string | null;
	};
	summary: BroadcastDeliveryReportSummary;
	data: BroadcastDeliveryReportRow[];
	total: number;
	page: number;
	limit: number;
	totalPages: number;
	hasDetailedReport: boolean;
}

@Injectable({
	providedIn: "root",
})
export class HttpHelpService {
	curentLang = new BehaviorSubject(null);

	headers: any;
	baseUrl: any;
	sortColumn: any;
	sortDirection: string;
	constructor(private _HttpClient: HttpClient, private Router: Router) {
		// this.curentLang.next(localStorage.getItem('curentLang')||'ar');
	}

	/* global function */
	getFilteredInvoices(
		fromDate: string,
		toDate: string,
		merchantId: number,
		sort: string
	): Observable<any> {
		const params = new HttpParams()
			.set("fromDate", fromDate)
			.set("toDate", toDate)
			.set("merchant_id", merchantId.toString())
			.set("sort", sort);

		return this._HttpClient.get<any>(`${baseUrl}invoice/filter`, { params });
	}
	/* start priviliges functions*/
	getPrivileges(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}privileges/leftMenu`);
	}
	getAllPrivilegesScreen(): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}privileges/getAllPrivilegesScreen`
		);
	}

	/* end priviliges functions*/

	/* start plans functions*/

	getAllActivePlans(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}plan/active`);
	}

	getPlanById(id): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}plan/${id}`);
	}

	getAllPans(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}plan`);
	}

	changePlanStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}plan/update-status/${id}`,
			model
		);
	}

	changeShowSignatureCompany(id: number, model: any): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}company/changeShowSignatureCompany/${id}/${model}`
		);
	}

	updatePlanById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}plan/${id}`, model);
	}

	/*  end plans functions*/
	/* start admin invoice-subscriptions-v2 plans */
	getAdminV2MainPlans(): Observable<any[]> {
		return this._HttpClient.get<any[]>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans`
		);
	}

	createAdminV2MainPlan(model: any): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans`,
			model
		);
	}

	updateAdminV2MainPlan(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/${id}`,
			model
		);
	}

	deleteAdminV2MainPlan(id: number): Observable<any> {
		return this._HttpClient.delete<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/${id}`
		);
	}

	getAdminV2DocExtensionPlans(): Observable<any[]> {
		return this._HttpClient.get<any[]>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/doc-extensions`
		);
	}

	createAdminV2DocExtensionPlan(model: any): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/doc-extensions`,
			model
		);
	}

	updateAdminV2DocExtensionPlan(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/doc-extensions/${id}`,
			model
		);
	}

	deleteAdminV2DocExtensionPlan(id: number): Observable<any> {
		return this._HttpClient.delete<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/doc-extensions/${id}`
		);
	}

	getAdminV2CompanyExtensionPlans(): Observable<any[]> {
		return this._HttpClient.get<any[]>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/company-extensions`
		);
	}

	createAdminV2CompanyExtensionPlan(model: any): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/company-extensions`,
			model
		);
	}

	updateAdminV2CompanyExtensionPlan(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/company-extensions/${id}`,
			model
		);
	}

	deleteAdminV2CompanyExtensionPlan(id: number): Observable<any> {
		return this._HttpClient.delete<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/company-extensions/${id}`
		);
	}

	seedAdminV2Plans(): Observable<{ created: number; skipped: number }> {
		return this._HttpClient.post<{ created: number; skipped: number }>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/seed`,
			{}
		);
	}
	/* end admin invoice-subscriptions-v2 plans */

	/* start admin merchant subscriptions v2 */
	createAdminMerchantSubscription(model: {
		merchant_id: number;
		plan_id: number;
		start_date: string;
		activation_mode: "admin_grant" | "payment_link";
		language?: "ar" | "en" | "he";
	}): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/merchant-subscriptions`,
			model
		);
	}

	getAdminMerchantSubscriptions(): Observable<any[]> {
		return this._HttpClient.get<any[]>(
			`${baseUrl}admin/invoice-subscriptions-v2/merchant-subscriptions`
		);
	}

	getAdminMerchantSubscriptionStatus(merchantId: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/merchant-subscriptions/${merchantId}`
		);
	}

	getAdminMerchantSubscriptionHistory(merchantId: number): Observable<any[]> {
		return this._HttpClient.get<any[]>(
			`${baseUrl}admin/invoice-subscriptions-v2/merchant-subscriptions/${merchantId}/history`
		);
	}

	disableAdminMerchantSubscription(merchantId: number): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/merchant-subscriptions/${merchantId}/disable`,
			{}
		);
	}

	createAdminMerchantDocExtension(model: {
		merchant_id: number;
		plan_id: number;
		activation_mode: "admin_grant" | "payment_link";
		language?: "ar" | "en" | "he";
	}): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/merchant-subscriptions/doc-extension`,
			model
		);
	}

	createAdminMerchantCompanyExtension(model: {
		merchant_id: number;
		plan_id: number;
		activation_mode: "admin_grant" | "payment_link";
		language?: "ar" | "en" | "he";
	}): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/merchant-subscriptions/company-extension`,
			model
		);
	}

	adjustAdminMerchantSubscription(merchantId: number, model: {
		extra_documents?: number;
		extra_companies?: number;
		extra_days?: number;
		extra_tokens?: number;
	}): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/subscriptions/${merchantId}/adjust`,
			model
		);
	}
	/* end admin merchant subscriptions v2 */

	/* admin custom payments (PayPlus links) */
	getAdminCustomPayments(params?: {
		merchant_id?: number;
		status?: string;
		page?: number;
		limit?: number;
	}): Observable<{ data: any[]; total: number; page: number; limit: number }> {
		let httpParams = new HttpParams();
		if (params?.merchant_id != null) {
			httpParams = httpParams.set("merchant_id", String(params.merchant_id));
		}
		if (params?.status) {
			httpParams = httpParams.set("status", params.status);
		}
		if (params?.page != null) {
			httpParams = httpParams.set("page", String(params.page));
		}
		if (params?.limit != null) {
			httpParams = httpParams.set("limit", String(params.limit));
		}
		return this._HttpClient.get<{ data: any[]; total: number; page: number; limit: number }>(
			`${baseUrl}admin/custom-payments`,
			{ params: httpParams }
		);
	}

	getAdminCustomPayment(id: number): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}admin/custom-payments/${id}`);
	}

	createAdminCustomPayment(model: {
		merchant_id: number;
		title: string;
		description?: string | null;
		amount: number;
		language?: string;
	}): Observable<any> {
		return this._HttpClient.post<any>(`${baseUrl}admin/custom-payments`, model);
	}

	deleteAdminCustomPayment(id: number): Observable<any> {
		return this._HttpClient.delete<any>(`${baseUrl}admin/custom-payments/${id}`);
	}
	/* end admin custom payments */

	/** Admin broadcast push notifications (persisted history + send). See Swagger: Admin - Notifications */
	listAdminBroadcastNotifications(params?: {
		page?: number;
		limit?: number;
		status?: string;
	}): Observable<{
		items: any[];
		total: number;
		page: number;
		limit: number;
		totalPages: number;
	}> {
		let httpParams = new HttpParams();
		if (params?.page != null) {
			httpParams = httpParams.set('page', String(params.page));
		}
		if (params?.limit != null) {
			httpParams = httpParams.set('limit', String(params.limit));
		}
		if (params?.status != null && params.status !== '') {
			httpParams = httpParams.set('status', params.status);
		}
		return this._HttpClient.get<{
			items: any[];
			total: number;
			page: number;
			limit: number;
			totalPages: number;
		}>(`${baseUrl}admin/notifications`, { params: httpParams });
	}

	getAdminBroadcastFcmDiagnostics(): Observable<{
		firebase_admin_ready: boolean;
		users_with_device_token: number;
		hint: string;
	}> {
		return this._HttpClient.get<any>(`${baseUrl}admin/notifications/fcm-diagnostics`);
	}

	getAdminBroadcastNotification(id: number): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}admin/notifications/${id}`);
	}

	createAdminBroadcastNotification(model: AdminBroadcastCreateModel): Observable<any> {
		return this._HttpClient.post<any>(`${baseUrl}admin/notifications`, model);
	}

	getAdminBroadcastDispatchProgress(token: string): Observable<{
		broadcastId: number;
		phase: string;
		recipients: { userId: number; label: string; at: number }[];
		done: boolean;
		error?: string;
	}> {
		return this._HttpClient.get<any>(
			`${baseUrl}admin/notifications/dispatch-progress/${encodeURIComponent(token)}`,
		);
	}

	sendAdminBroadcastNotification(
		id: number,
		body?: { resend?: boolean },
	): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}admin/notifications/${id}/send`,
			body ?? {},
		);
	}

	deleteAdminBroadcastNotification(id: number): Observable<any> {
		return this._HttpClient.delete<any>(`${baseUrl}admin/notifications/${id}`);
	}

	getAdminBroadcastDeliveryReport(
		id: number,
		params?: {
			page?: number;
			limit?: number;
			status?: BroadcastDeliveryStatus | '';
			search?: string;
		},
	): Observable<BroadcastDeliveryReportResponse> {
		let httpParams = new HttpParams();
		if (params?.page != null) {
			httpParams = httpParams.set('page', String(params.page));
		}
		if (params?.limit != null) {
			httpParams = httpParams.set('limit', String(params.limit));
		}
		if (params?.status) {
			httpParams = httpParams.set('status', params.status);
		}
		if (params?.search?.trim()) {
			httpParams = httpParams.set('search', params.search.trim());
		}
		return this._HttpClient.get<BroadcastDeliveryReportResponse>(
			`${baseUrl}admin/notifications/${id}/delivery-report`,
			{ params: httpParams },
		);
	}
	/* end admin broadcast notifications */

//get customer dropdown

getCustomersdrop(): Observable<any> {
	return this._HttpClient.get<any>(`${baseUrl}customers/select`);
}

/**
	 * Get all customers based on merchant for dropdown (loads all with high limit).
	 */
	getAllCustomersForDropdown(): Observable<any[]> {
		const params = new HttpParams().set('limit', '10000').set('offset', '0');
		return this._HttpClient.get<any>(`${baseUrl}customers/getAllCustomersBasedOnMerchant`, { params })
			.pipe(map(res => this.extractArray(res, 'customers')));
	}

	/* start subscribe-to-plans functions*/
	getAnalyticsSubscribeToPlan(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}dashboard/statistics`);
	}

	getSubscribeDetailsByMerchantId(): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}subscribe-to-plans/getSubscribeDetailsByMerchantId`
		);
	}

	Post(endPoint: string, model: any): Observable<any> {
		return this._HttpClient.post<any>(baseUrl + endPoint, model);
	}

	Get(endPoint: string, params?: any): Observable<any> {
		return this._HttpClient.get<any>(baseUrl + endPoint, { params });
	}

	increasePlanDetailsByAdmin(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}subscribe-to-plans/increasePlanDetailsByAdmin/${id}`,
			model
		);
	}
	/*  end subscribe-to-plans functions*/

	/* start merchant functions */

	getAllMerchantInfo(): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}merchant-info/getAllMerchantInfoByBasedToken`
		);
	}

	getMerchantInfoById(id): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}merchant-info/${id}`);
	}

	updateMerchantById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}merchant-info/updateMerchantInfo/${id}`,
			model
		);
	}

	changeMerchantStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}merchant-info/updateStatusMerchantByAdmin/${id}`,
			model
		);
	}
	/* end merchant functions */

	/* start company functions */
	getAllCompaniesExcel(): Observable<Blob> {
		return this._HttpClient.get(`${baseUrl}company/companiesExcel`, {
			responseType: "blob",
		});
	}

	getAllCompaniesInfo(
		limit: number,
		offset: number,
		keyword?: string,
		includeArchived?: boolean,
		recordType: 'merchant' | 'accountant' | 'all' = 'merchant'
	): Observable<any> {
		let params = new HttpParams()
			.set("limit", limit.toString())
			.set("offset", offset.toString())
			.set("recordType", recordType);

		if (keyword) {
			params = params.set("keyword", keyword);
		}

		if (includeArchived) {
			params = params.set("includeArchived", "true");
		}

		return this._HttpClient.get<any>(
			`${baseUrl}company/getALLCompanyByBasedToken`,
			{ params }
		);
	}

	getAllActiveCompaniesInfoByMerchantId(): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}company/getAllActiveCompanyByMerchant`
		);
	}
	uploadCompanyImage(id: number, model: any): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}company/updateLogo/${id}`,
			model
		);
	}

	/*getCompanyInfoById(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}company/getCompanyByCompanyId/${id}`
		);
	}*/

	// ...template...
	getTemplatesSelect(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}templates/select`);
	}
	// ...template...

	getSequencesByCompanyId(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}company/getSequencesByCompanyId/${id}`
		);
	}
	uploadCompanySeal(id: number, model: any): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}company/updateSeal/${id}`,
			model
		);
	}

	changeShowSealCompany(id: number, model: any): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}company/changeShowSealCompany/${id}/${model}`
		);
	}

	getCompanyInfoById(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}company/getCompanyByCompanyId/${id}`
		);
	}

	updateSequenceValue(model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}company/updateSequenceValue`,
			model
		);
	}

	getCompanyIdByUuid(company_uuid: string): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}company/getCompanyId/${company_uuid}`
		);
	}

	getActivityInCompany(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}company/getActivityInCompany/${id}`
		);
	}

	updateCompanyById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}company/updateCompanyByCompanyId/${id}`,
			model
		);
	}

	updateCompanyByAdminByCompanyId(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}company/updateCompanyByAdmin/${id}`,
			model
		);
	}

	deleteCompanyByCompanyId(id: number): Observable<any> {
		return this._HttpClient.delete<any>(
			`${baseUrl}company/deleteCompanyByCompanyId/${id}`
		);
	}

	deleteCompanyLogoByCompanyId(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}company/removeLogoCompany/${id}`
		);
	}

	archiveCompany(id: number): Observable<any> {
		return this._HttpClient.post<any>(`${baseUrl}company/${id}/archive`, {});
	}

	restoreCompany(id: number): Observable<any> {
		return this._HttpClient.post<any>(`${baseUrl}company/${id}/restore`, {});
	}

	getArchivedCompanies(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}company/archived`);
	}

	getCompanyLinksByMerchantCompany(merchantCompanyId: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}company-links/by-merchant-company/${merchantCompanyId}`
		);
	}

	/*  end company functions */

	/* start vat functions */
	getAllActiveVatList(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}vat-list/active`);
	}

	getAllVatList(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}vat-list`);
	}

	changeVatStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}vat-list/status/${id}`, model);
	}

	updateVatById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}vat-list/${id}`, model);
	}

	/*  end vat functions */

	/* Admin — invoice v2 token extension plans (catalog) */
	getAdminV2TokenExtensionPlans(): Observable<any[]> {
		return this._HttpClient.get<any[]>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/token-extensions`
		);
	}

	createAdminV2TokenExtensionPlan(model: any): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/token-extensions`,
			model
		);
	}

	updateAdminV2TokenExtensionPlan(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/token-extensions/${id}`,
			model
		);
	}

	deleteAdminV2TokenExtensionPlan(id: number): Observable<any> {
		return this._HttpClient.delete<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/plans/token-extensions/${id}`
		);
	}

	createAdminMerchantTokenExtension(model: {
		merchant_id: number;
		plan_id: number;
		activation_mode: "admin_grant" | "payment_link";
		language?: "ar" | "en" | "he";
	}): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/merchant-subscriptions/token-extension`,
			model
		);
	}

	getAdminV2SubscriptionOrders(merchantId: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}admin/invoice-subscriptions-v2/subscriptions/${merchantId}/orders`
		);
	}

	/* start vat details functions */

	getVatPercentageByVatListIdAndDate(id: number, date: any): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}vat-details/getVatPercentageByVatListIdAndDate/${id}/${date}`
		);
	}

	getAllVatDetailsByVatListId(id): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}vat-details/getAllVatDetailsByVatListId/${id}`
		);
	}

	updateVatDetailsByID(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}vat-details/${id}`, model);
	}

	deleteVatDetailsByID(id: number): Observable<any> {
		return this._HttpClient.delete<any>(`${baseUrl}vat-details/${id}`);
	}

	/*  end vat details functions */

	/* start banks functions */

	getAllBanks(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}banks`);
	}

	getActiveBanks(): Observable<any[]> {
		return this._HttpClient.get<any>(`${baseUrl}banks/getActiveBanks`).pipe(
			map((response) => filterActiveBanks(normalizeApiListResponse(response))),
		);
	}

	getBankById(id): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}banks/${id}`);
	}

	updateBankById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}banks/${id}`, model);
	}

	changeBankStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}banks/status/${id}`, model);
	}

	deleteBankByID(id: number): Observable<any> {
		return this._HttpClient.delete<any>(`${baseUrl}banks/${id}`);
	}

	/* end banks functions */

	/* start banks branches functions */

	getAllBankBranchById(id): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}banks-branches/getAllBankBranchById/${id}`
		);
	}

	getAllBankBranch(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}banks-branches`);
	}

	updateBankBranchById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}banks-branches/${id}`, model);
	}

	deleteBankBranchByID(id: number): Observable<any> {
		return this._HttpClient.delete<any>(`${baseUrl}banks-branches/${id}`);
	}

	/* end banks branches functions */

	/* start payment method functions */

	getAllPaymentMethod(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}payment-methods`);
	}

	getAllActivePaymentMethods(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}payment-methods/active`);
	}

	getPaymentMethodById(id): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}payment-methods/getPaymentMethodById/${id}`
		);
	}

	updatePaymentMethodById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}payment-methods/updatePaymentMethodById/${id}`,
			model
		);
	}

	deletePaymentMethodById(id: number): Observable<any> {
		return this._HttpClient.delete<any>(
			`${baseUrl}payment-methods/deletePaymentMethodById/${id}`
		);
	}

	/*  start categories functions */

	getAllCategories(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}categories`);
	}
	getAllActiveCategories(): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}categories/getActiveCategories`
		);
	}

	changeCategoryStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}categories/status/${id}`,
			model
		);
	}

	updateCategoryById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}categories/${id}`, model);
	}

	/*  end categories functions */

	/* start lookupdetails functions */
	getAllStatus(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}lookup-det/getAllStatus`);
	}

	getAllCustomerType(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}lookup-det/getAllCustomerType`);
	}

	getAllUserType(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}lookup-det/getAllUserType`);
	}

	getAllStatusSubscribed(): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}lookup-det/getAllStatusSubscribed`
		);
	}

	/*  end lookupdetails functions */

	/*  start users functions */

	getProfileInfo(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}users/getProfileInfo`);
	}

	getUserById(id: any): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}users/${id}`);
	}

	updateUserLangById(model: any): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}users/updateLanguage`, model);
	}

	/** FCM / device token for push (Firebase Web). */
	updateUserDeviceToken(model: {
		device_type?: string;
		device_token: string;
		device_id?: string;
	}): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}users/updateDevice`, model);
	}

	getCurrentPushDeviceStatus(): Observable<{
		status: number;
		data?: {
			device_id: string;
			device_type: string | null;
			notifications_enabled: boolean;
			has_device_token: boolean;
		};
	}> {
		return this._HttpClient.get<any>(`${baseUrl}users/push-device/current`);
	}

	enableCurrentPushDevice(model: {
		device_type?: string;
		device_token: string;
	}): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}users/push-device/current/enable`, model);
	}

	disableCurrentPushDevice(): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}users/push-device/current/disable`, {});
	}

	updateUserName(model: any): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}users/updateUserName`, model);
	}

	updateUserInfo(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}users/updateUserInfo/${id}`,
			model
		);
	}

	getALLUsersByBasedToken(limit = 10, offset = 0, search?: string): Observable<any> {
		let params = new HttpParams()
			.set('limit', limit.toString())
			.set('offset', offset.toString());
		if (search) params = params.set('search', search);
		return this._HttpClient.get<any>(`${baseUrl}users/getALLUsersByBasedToken`, { params });
	}

	getAllPlatformUsers(params?: {
		offset?: number;
		limit?: number;
		search?: string;
	}): Observable<{ data: any[]; total: number; limit: number; offset: number }> {
		let httpParams = new HttpParams();
		if (params?.offset != null) {
			httpParams = httpParams.set('offset', String(params.offset));
		}
		if (params?.limit != null) {
			httpParams = httpParams.set('limit', String(params.limit));
		}
		if (params?.search) {
			httpParams = httpParams.set('search', params.search);
		}
		return this._HttpClient.get<{ data: any[]; total: number; limit: number; offset: number }>(
			`${baseUrl}users/admin/all-platform-users`,
			{ params: httpParams }
		);
	}

	getAllUserByOfficeId(office_id: any, limit = 10, offset = 0, search?: string): Observable<any> {
		let params = new HttpParams()
			.set('limit', limit.toString())
			.set('offset', offset.toString());
		if (search) params = params.set('search', search);
		return this._HttpClient.get<any>(
			`${baseUrl}users/getAllUserByOfficeId/${office_id}`,
			{ params }
		);
	}

	getAllUserByMerchantId(merchant_id: any, limit = 10, offset = 0, search?: string): Observable<any> {
		let params = new HttpParams()
			.set('limit', limit.toString())
			.set('offset', offset.toString());
		if (search) params = params.set('search', search);
		return this._HttpClient.get<any>(
			`${baseUrl}users/getAllUserByMerchantId/${merchant_id}`,
			{ params }
		);
	}

	changeUserStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}users/updateUserStatus/${id}`,
			model
		);
	}

	updateUserImage(id: number, model: any): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}users/updateUserImage/${id}`,
			model
		);
	}

	ChangeUserPasswordByUserId(model: any): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}users/ChangeUserPasswordByUserId`,
			model
		);
	}

	ChangeUserPasswordByAdmin(model: any): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}users/ChangeUserPasswordByAdmin`,
			model
		);
	}

	updateUserToken(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}users/updateToken`);
	}

	getAutoLoginUrl(userId: number): Observable<any> {
		// Use the new endpoint for autologin
		return this._HttpClient.get<any>(`${baseUrl}admin/auth/auto-login-url/${userId}`);
	}

	/* Admin Security Code */
	getSecurityCode(body: { user_id: number; admin_secret: string }): Observable<any> {
		return this._HttpClient.post<any>(`${baseUrl}auth/admin/security-code/get`, body);
	}

	sendSecurityCode(body: { user_id: number; admin_secret: string }): Observable<any> {
		return this._HttpClient.post<any>(`${baseUrl}auth/admin/security-code/send`, body);
	}

	changeSecurityCode(body: { user_id: number; admin_secret: string }): Observable<any> {
		return this._HttpClient.post<any>(`${baseUrl}auth/admin/security-code/change`, body);
	}

	/*  end users functions */

	/* start users privilieg functions */
	getAllPrivilegesByUser(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}users-privileges/getAllPrivilegesByUser/${id}`,
			model
		);
	}

	getAllPrivileges(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}users-privileges/getAllPrivilegesByUser/${id}`
		);
	}

	changePrivilegeStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}users-privileges/status/${id}`,
			model
		);
	}

	deletePrivilege(id: number): Observable<any> {
		return this._HttpClient.delete<any>(`${baseUrl}users-privileges/${id}`);
	}

	/* start customers functions*/

	getAllCustomersBasedOnMerchant(
		limit: number,
		offset: number,
		search?: string
	): Observable<{ customers: any[]; total: number; limit: number; offset: number }> {
		let params = new HttpParams()
			.set('limit', limit.toString())
			.set('offset', offset.toString());
		if (search && search.trim()) {
			params = params.set('search', search.trim());
		}
		return this._HttpClient.get<any>(
			`${baseUrl}customers/getAllCustomersBasedOnMerchant`,
			{ params }
		).pipe(map((res) => this.normalizeCustomersPageResponse(res, limit, offset)));
	}

	/** Normalise paginated customer list responses from API variants. */
	normalizeCustomersPageResponse(
		res: any,
		fallbackLimit?: number,
		fallbackOffset?: number,
	): { customers: any[]; total: number; limit: number; offset: number } {
		const payload = res?.data && typeof res.data === 'object' && !Array.isArray(res.data)
			? res.data
			: res;
		const customers = this.extractArray(payload ?? res, 'customers');
		const parsedTotal = Number(
			payload?.total ??
			payload?.count ??
			payload?.totalRecords ??
			payload?.total_count ??
			res?.total ??
			res?.count ??
			NaN,
		);
		const total = Number.isFinite(parsedTotal) && parsedTotal >= 0
			? parsedTotal
			: customers.length;
		const limit = Number(payload?.limit ?? res?.limit ?? fallbackLimit ?? customers.length) || customers.length || 20;
		const offset = Number(payload?.offset ?? res?.offset ?? fallbackOffset ?? 0) || 0;
		return { customers, total, limit, offset };
	}

	/** Returns an array of active customers. */
	getAllActiveCustomersBasedOnMerchantId(limit = 10000, offset = 0, search?: string): Observable<any[]> {
		let params = new HttpParams()
			.set('limit', limit.toString())
			.set('offset', offset.toString());
		if (search && search.trim()) {
			params = params.set('search', search.trim());
		}
		return this._HttpClient.get<any>(
			`${baseUrl}customers/getAllActiveCustomersBasedOnMerchantId`,
			{ params }
		).pipe(map((res: any) => res?.customers ?? res ?? []));
	}

	getCustomersList(filters: {
		search?: string;
		company_id?: number;
		customer_type?: number;
		customer_status?: number;
		customer_phone?: string;
		customer_email?: string;
		customer_vat_number?: string;
		custom_id?: string;
		city?: string;
		id_number?: string;
		limit?: number;
		offset?: number;
	}): Observable<any> {
		let params = new HttpParams();
		if (filters.search) params = params.set('search', filters.search);
		if (filters.company_id != null) params = params.set('company_id', filters.company_id.toString());
		if (filters.customer_type != null) params = params.set('customer_type', filters.customer_type.toString());
		if (filters.customer_status != null) params = params.set('customer_status', filters.customer_status.toString());
		if (filters.customer_phone) params = params.set('customer_phone', filters.customer_phone);
		if (filters.customer_email) params = params.set('customer_email', filters.customer_email);
		if (filters.customer_vat_number) params = params.set('customer_vat_number', filters.customer_vat_number);
		if (filters.custom_id) params = params.set('custom_id', filters.custom_id);
		if (filters.city) params = params.set('city', filters.city);
		if (filters.id_number) params = params.set('id_number', filters.id_number);
		if (filters.limit != null) params = params.set('limit', filters.limit.toString());
		if (filters.offset != null) params = params.set('offset', filters.offset.toString());
		return this._HttpClient.get<any>(`${baseUrl}customers/list`, { params });
	}

	updateCustomersById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}customers/${id}`, model);
	}

	changeCustomerStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}customers/updateStatusCustomer/${id}`,
			model
		);
	}

	/** Normalise any paginated customer/product response into a plain array. */
	private extractArray(res: any, key = 'customers'): any[] {
		if (Array.isArray(res)) return res;
		if (Array.isArray(res?.[key])) return res[key];
		if (Array.isArray(res?.data)) return res.data;
		if (Array.isArray(res?.data?.[key])) return res.data[key];
		return [];
	}

	getCustomersByCompanyId(
		companyId: number,
		limit = 10000,
		offset = 0,
		search?: string
	): Observable<any[]> {
		let params = new HttpParams()
			.set('limit', limit.toString())
			.set('offset', offset.toString());
		if (search != null && search.trim() !== '') {
			params = params.set('search', search.trim());
		}
		return this._HttpClient.get<any>(
			`${baseUrl}customers/getCustomersByCompanyId/${companyId}`,
			{ params }
		).pipe(map(res => this.extractArray(res, 'customers')));
	}

	getCustomerById(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}customers/getCustomerById/${id}`
		);
	}

	deleteCustomerById(id: number): Observable<any> {
		return this._HttpClient.delete<any>(`${baseUrl}customers/${id}`);
	}

	/*  end customers functions*/

	/* start products functions*/

	getAllProductsBasedOnMerchant(
		limit: number,
		offset: number,
		search?: string
	): Observable<any> {
		let params = new HttpParams()
			.set("limit", limit.toString())
			.set("offset", offset.toString());
		if (search?.trim()) {
			params = params.set('search', search.trim());
		}
		return this._HttpClient.get<any>(
			`${baseUrl}products/getAllProductsBasedOnMerchant`,
			{ params }
		);
	}

	updateProductsById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}products/${id}`, model);
	}

	changeProductStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}products/updateStatusProduct/${id}`,
			model
		);
	}

	getAllActiveProductsBasedOnMerchantId(
		limit = 10000,
		offset = 0,
		search?: string,
		company_id?: number,
	): Observable<any[]> {
		return this.getActiveProductsForPicker(limit, offset, search, company_id).pipe(
			map((page) => page.products),
		);
	}

	/** Active products plus the unpaged total, so a picker can tell when it holds the full catalog. */
	getActiveProductsForPicker(
		limit = 2000,
		offset = 0,
		search?: string,
		company_id?: number,
	): Observable<{ products: any[]; total: number }> {
		let params = new HttpParams()
			.set('limit', limit.toString())
			.set('offset', offset.toString());
		if (search?.trim()) {
			params = params.set('search', search.trim());
		}
		if (company_id != null) {
			params = params.set('company_id', company_id.toString());
		}
		return this._HttpClient.get<any>(
			`${baseUrl}products/getAllActiveProductsBasedOnMerchantId`,
			{ params }
		).pipe(
			map((res) => {
				const products = this.extractArray(res, 'products');
				const totalCandidate = Number(
					res?.total ?? res?.count ?? res?.data?.total ?? res?.data?.count,
				);
				return {
					products,
					total: Number.isFinite(totalCandidate) ? totalCandidate : products.length,
				};
			}),
		);
	}

	getPosProducts(
		limit = 50,
		offset = 0,
		search?: string,
		company_id?: number
	): Observable<any> {
		let params = new HttpParams()
			.set('limit', limit.toString())
			.set('offset', offset.toString());
		if (search?.trim()) {
			params = params.set('search', search.trim());
		}
		if (company_id != null) {
			params = params.set('company_id', company_id.toString());
		}
		return this._HttpClient.get<any>(`${baseUrl}products/pos`, { params });
	}

	uploadProductImage(productId: number, formData: FormData): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}products/${productId}/upload-image`,
			formData
		);
	}

	aiGenerateProductImage(body: {
		merchant_id: number;
		prompt?: string;
		product_name?: string;
	}): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}products/ai-generate-image`,
			body
		);
	}

	approveAiProductImage(
		productId: number,
		body: { preview_key: string }
	): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}products/${productId}/approve-ai-image`,
			body
		);
	}

	deleteProductById(id: number): Observable<any> {
		return this._HttpClient.delete<any>(`${baseUrl}products/${id}`);
	}

	downloadProductsTemplate(): Observable<Blob> {
		return this._HttpClient.get(`${baseUrl}products/download-template`, {
			responseType: 'blob',
		});
	}

	uploadProductsExcel(formData: FormData): Observable<any> {
		return this._HttpClient.post<any>(`${baseUrl}products/upload-excel`, formData);
	}

	/*  end products functions*/

	/* start merchant-product-categories */

	getMerchantProductCategories(search?: string): Observable<any> {
		let params = new HttpParams();
		if (search?.trim()) {
			params = params.set('search', search.trim());
		}
		return this._HttpClient.get<any>(`${baseUrl}merchant-product-categories`, { params });
	}

	getActiveMerchantProductCategories(search?: string): Observable<any> {
		let params = new HttpParams();
		if (search?.trim()) {
			params = params.set('search', search.trim());
		}
		return this._HttpClient.get<any>(`${baseUrl}merchant-product-categories/active`, { params });
	}

	getMerchantProductCategoryById(id: number): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}merchant-product-categories/${id}`);
	}

	updateMerchantProductCategoryById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}merchant-product-categories/${id}`, model);
	}

	changeMerchantProductCategoryStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}merchant-product-categories/${id}/status`,
			model
		);
	}

	deleteMerchantProductCategoryById(id: number): Observable<any> {
		return this._HttpClient.delete<any>(`${baseUrl}merchant-product-categories/${id}`);
	}

	uploadMerchantProductCategoryImage(id: number, formData: FormData): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}merchant-product-categories/${id}/upload-image`,
			formData
		);
	}

	aiGenerateMerchantProductCategoryImage(body: {
		merchant_id: number;
		name?: string;
		prompt?: string;
	}): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}merchant-product-categories/ai-generate-image`,
			body
		);
	}

	approveAiMerchantProductCategoryImage(
		id: number,
		body: { preview_key: string }
	): Observable<any> {
		return this._HttpClient.post<any>(
			`${baseUrl}merchant-product-categories/${id}/approve-ai-image`,
			body
		);
	}

	/*  end merchant-product-categories */

	/* start measurement-units functions*/

	getAllActiveMeasurementUnits(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}measurement-units/active`);
	}

	getAllMeasurementUnits(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}measurement-units`);
	}

	changeMeasurementUnitsStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}measurement-units/update-status/${id}`,
			model
		);
	}

	deleteMeasurementUnitById(id: number): Observable<any> {
		return this._HttpClient.delete<any>(`${baseUrl}measurement-units/${id}`);
	}

	updateMeasurementById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}measurement-units/${id}`,
			model
		);
	}

	/*  end measurement-units functions*/

	/* start invoices-types functions*/

	getAllInvoicesTypes(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}invoice-type-list`);
	}

	getInvoicesAndReceiptsByInvoiceid(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/getInvoicesAndReceiptsByInvoiceUuid/${id}`
		);
	}

	getAllActiveInvoicesTypes(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}invoice-type-list/active`);
	}

	changeInvoicesTypesStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}invoice-type-list/status/${id}`,
			model
		);
	}

	deleteInvoicesTypesById(id: number): Observable<any> {
		return this._HttpClient.delete<any>(`${baseUrl}invoice-type-list/${id}`);
	}

	updateInvoicesTypesById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}invoice-type-list/${id}`,
			model
		);
	}

	/*  end invoices-types functions*/

	/* add receipt functions */

	getReceiptById(id: number): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}receipt/getReceiptById/${id}`);
	}

	getAllReceiptsByMerchantId(
		id: number,
		limit: number,
		offset: number
	): Observable<any> {
		const params = new HttpParams()
			.set("limit", limit.toString())
			.set("offset", offset.toString());
		return this._HttpClient.get<any>(
			`${baseUrl}receipt/getAllReceiptsByMerchantId/${id} `,
			{ params }
		);
	}

	getReceiptPdfById(id: number): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}receipt/getPdf/${id}`);
	}

	updateVersionCopyReceipt(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}receipt/updateVersionCopy/${id}`
		);
	}

	updateReceiptById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}receipt/updateReceipt/${id}`,
			model
		);
	}

	deleteReceiptById(id: number): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}receipt/deleteReceipt/${id}`);
	}

	/* end add receipt functions */

	/* start app info functions */

	getAppInfoData(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}app-info`);
	}

	updateAppInfoData(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}app-info/${id}`, model);
	}

	/* end app info functions */

	/* start invoices functions*/

	getALLInvoiceByBasedMerchantId(
		limit: number,
		offset: number
	): Observable<any> {
		const params = new HttpParams()
			.set("limit", limit.toString())
			.set("offset", offset.toString());

		return this._HttpClient.get<any>(
			`${baseUrl}invoice/getALLInvoiceByBasedMerchantId`,
			{ params }
		);
	}

	getAllFinancialRequestByMerchantId(
		limit?: any,
		offset?: any
	): Observable<any> {
		const params = new HttpParams()
			.set("limit", limit.toString())
			.set("offset", offset.toString());
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/getAllFinancialRequestByMerchantId`,
			{ params }
		);
	}

	getAllReverseInvoiceByMerchantId(limit?: any, offset?: any): Observable<any> {
		const params = new HttpParams()
			.set("limit", limit.toString())
			.set("offset", offset.toString());
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/getAllReverseInvoiceByMerchantId`,
			{ params }
		);
	}

	getAllInvoicesAndReceiptsBasedMerchantId(): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/getAllInvoicesAndReceiptsBasedMerchantId`
		);
	}

	getInvoicesFilters(
		fromDate: any,
		toDate: any,
		companyId?: number,
		limit?: any,
		offset?: number,
		invoice_type?: any,
		merchant_id?: any,
		searchKeyword?: string
	): Observable<any> {
		let params = new HttpParams();

		if (fromDate) {
			params = params.set("fromDate", fromDate?.toString());
		}

		if (toDate) {
			params = params.set("toDate", toDate?.toString());
		}

		if (companyId) {
			params = params.set("companyId", companyId?.toString());
		}

		if (limit) {
			params = params.set("limit", limit?.toString());
		}

		if (offset) {
			params = params.set("offset", offset?.toString());
		}

		if (invoice_type !== undefined && invoice_type !== null) {
			params = params.set("invoice_type", invoice_type?.toString());
		}

		if (merchant_id) {
			params = params.set("merchant_id", merchant_id?.toString());
		}

		if (searchKeyword) {
			params = params.set("searchKeyword", searchKeyword);
		}

		return this._HttpClient.get<any>(`${baseUrl}invoice/filter`, { params });
	}

	getInvoicesByInvoiceId(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/getInvoicesByInvoiceId/${id}`
		);
	}

	PostInvoiceById(id: number): Observable<any> {
		return this._HttpClient.post<any>(
		  `${baseUrl}invoice/${id}/cancel-invoice-receipt`, null
		);
	}

	deleteInvoiceById(id: number): Observable<any> {
		return this._HttpClient.delete<any>(
			`${baseUrl}invoice/${id}`
		);
	}

	exportPdfHtml(invoiceId: any, lang: string): Observable<Blob> {
		return this._HttpClient.get(
			`${baseUrl}export-pdf/html/${invoiceId}/${lang}`,
			{
				responseType: "blob", // تصحيح استخدام 'blob' فقط بدون 'as json'
			}
		);
	}

	confirmInvoice(id: any): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}invoice/confirmInvoice/${id}`);
	}

	convrtFinaicalRequestToInvoice(id: any): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/convrtFinaicalRequestToInvoice/${id}`
		);
	}
	// confirmInvoice(invoiceId: string): Observable<any> {
	//    return this._HttpClient.post<any>(`${baseUrl}/confirmInvoice`,{invoiceId}).pipe(
	//     tap(res => {
	//     }),
	//     catchError(error => {
	//       return throwError(error);
	//     })
	//   );
	// }

	getPdfByInvoiceId(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/getPdfByInvoiceId/${id}`
		);
	}

	updateVersionCopy(id: any): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/updateVersionCopy/${id}`
		);
	}

	downloadDocumentPdf(invoiceId: any, lang: string): Observable<Blob> {
		return this._HttpClient.get(
			`${baseUrl}export-pdf/Invoice-pdf/${invoiceId}/${lang}`,
			{
				responseType: "blob", // تصحيح استخدام 'blob' فقط بدون 'as json'
			}
		);
	}

	/*downloadDocumentPdf(invoiceId: any, lang: string): Observable<Blob> {
		return this._HttpClient.get(`${baseUrl}export-pdf/Invoice-pdf/${invoiceId}/${lang}`, {
			responseType: 'blob' فقط بدون 'as json'
		});
	};
*/
	downloadAllInvoicesPdf(
		company_id: number,
		lang: string,
		fromDate: string,
		toDate: string
	): Observable<HttpResponse<Blob>> {
		const url = `${baseUrl}export-pdf/printAllInvoices`;

		// إعداد المعلمات
		const params = {
			company_id: company_id.toString(),
			lang: lang,
			fromDate: fromDate,
			toDate: toDate,
		};

		// استدعاء الـ API
		return this._HttpClient.get<Blob>(url, {
			params: params,
			responseType: "blob" as "json", // تحديد نوع الاستجابة كـ Blob
			observe: "response", // استرجاع الاستجابة الكاملة (status, headers, body)
		});
	}

	GetCustomer(): Observable<any> {
		return this.getAllCustomersBasedOnMerchant(10000, 0);
	}

	GetPaymentMethodList(): Observable<any> {
		return this._HttpClient.get(`${baseUrl}payment-methods`);
	}

	GetBankList(): Observable<any[]> {
		return this.getActiveBanks();
	}
	GetCompany(): Observable<any> {
		return this._HttpClient.get(
			`${baseUrl}company/getAllActiveCompanyByMerchant?sortBy=createdDate&order=desc`
		);
	}

	/** Returns all companies including archived ones (for filter/view contexts). */
	GetAllCompaniesIncludingArchived(): Observable<any> {
		return this._HttpClient.get(
			`${baseUrl}company/getAllActiveCompanyByMerchant?sortBy=createdDate&order=desc&includeArchived=true`
		);
	}
	GetCustomerbyId(customer_id: any) {
		return this._HttpClient.get(
			`${baseUrl}customers/getCustomerById/${customer_id}`
		);
	}

	GetCompanybyId(company_id: any) {
		return this._HttpClient.get(
			`${baseUrl}company/getCompanyByCompanyId/${company_id}`
		);
	}
	GetProducts() {
		return this.getAllProductsBasedOnMerchant(10000, 0);
	}
	GetVatTypeList() {
		return this._HttpClient.get(`${baseUrl}vat-list/active`);
	}

	GetInvoiceTypeList() {
		return this._HttpClient.get(`${baseUrl}invoice-type-list/active`);
	}

	/** Yearly ceiling (before VAT) for tax-authority rules — GET yearly-max-amount?year=YYYY */
	getYearlyMaxAmount(year: number): Observable<any> {
		const params = new HttpParams().set('year', String(year));
		return this._HttpClient.get(`${baseUrl}yearly-max-amount`, { params });
	}

	getExpenseCategories() {
		return this._HttpClient.get(`${baseUrl}expense-categories`);
	}

	getAllBranchById(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}banks-branches/getAllBankBranchById/${id}`
		);
	}

	getBranchById(id: number): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}banks-branches/${id}`);
	}

	GetProductbycode(code: any) {
		return this._HttpClient.get(`${baseUrl}products/getProductById/` + code);
	}

	SaveInvoice(invoicedata: any) {
		if (invoicedata?.invoice_uuid) {
			// Update existing invoice by UUID
			return this._HttpClient.put(`${baseUrl}invoice/${invoicedata.invoice_uuid}`, invoicedata);
		} else {
			// Create new invoice
			return this._HttpClient.post(`${baseUrl}invoice`, invoicedata);
		}
	}

	/**
	 * Get the maximum amount for a specific year
	 * @param year The year for which to get the maximum amount
	 * @returns An observable containing the maximum amount for the specified year
	 */
	GetYearlyMaxAmount(year: number) {
		const params = new HttpParams().set("year", year.toString());
		return this._HttpClient.get(`${baseUrl}yearly-max-amount`, { params });
	}

	GetYear(year: any) {
		return this._HttpClient.get(`${baseUrl}yearly-max-amount/year/${year}`);
	}

	SaveReceipt(receiptdata: any) {
		return this._HttpClient.post(`${baseUrl}receipt`, receiptdata);
	}

	SaveUpdatedInvoice(id: any, invoicedata: any) {
		return this._HttpClient.put(`${baseUrl}invoice/${id}`, invoicedata);
	}

	updateInvoiceAndReceipt(id: any, invoicedata: any) {
		return this._HttpClient.put(
			`${baseUrl}invoice/updateInvoiceAndRecipte/${id}`,
			invoicedata
		);
	}

	createInvoiceAndReceipt(invoicedata: any) {
		return this._HttpClient.post(
			`${baseUrl}invoice/createInvoiceAndReceipt`,
			invoicedata
		);
	}

	GetInvoiceForUpdate(InvoiceId: any) {
		return this._HttpClient.get(
			`${baseUrl}invoice/getInvoicesByInvoiceId/` + InvoiceId
		);
	}
	getInvoicesAndReceiptsByInvoiceId(InvoiceId: any) {
		return this._HttpClient.get(
			`${baseUrl}invoice/getInvoicesAndReceiptsByInvoiceId/` + InvoiceId
		);
	}

	convertTheInvoiceToReverseInvoice(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/convertTheInvoiceToReverseInvoice/${id}`
		);
	}

	convertTheInvoiceToReverseInvoiceAllDocuments(invoiceId: number): Observable<any> {
		return this._HttpClient.post<any>(
				`${baseUrl}invoice/${invoiceId}/cancle-receipt`,
				null 
		);
	}


	updateTokenBody(id: any, updateTokenBody: any) {
		return this._HttpClient.put(`${baseUrl}oauth/${id}`, updateTokenBody);
	}

	refreshAccessToken(
		refreshToken: string,
		clientId: string,
		clientSecret: string
	): Observable<any> {
		const tokenUrl = environment.tokenUrl; // استخدام tokenUrl من ملف البيئة

		const body = new URLSearchParams();
		body.set("grant_type", "refresh_token");
		body.set("refresh_token", refreshToken);
		body.set("client_id", clientId);
		body.set("client_secret", clientSecret);
		body.set("scope", "scope");

		const headers = new HttpHeaders({
			"Content-Type": "application/x-www-form-urlencoded",
		});

		return this._HttpClient.post(tokenUrl, body.toString(), { headers });
	}

	exchangeCodeForToken(
		code: string,
		clientId: string,
		clientSecret: string,
		redirectUri: string,
		scope: string
	): Observable<any> {
		const tokenUrl = environment.tokenUrl; // استخدام tokenUrl من ملف البيئة

		const body = new URLSearchParams({
			grant_type: "authorization_code",
			code: code,
			redirect_uri: redirectUri,
			client_id: clientId,
			client_secret: clientSecret,
			scope: scope,
		}).toString();

		// Create the authorization header using Base64 encoding
		const authHeader = `Basic ${btoa(`${clientId}:${clientSecret}`)}`;

		// Prepare headers for the HTTP request
		const headers = new HttpHeaders({
			"Content-Type": "application/x-www-form-urlencoded",
			Authorization: authHeader,
		});

		// Make the HTTP post request to the token URL
		return this._HttpClient.post(tokenUrl, body, { headers });
	}

	SaveTokenData(tokenData: any) {
		return this._HttpClient.post(`${baseUrl}oauth/save-token`, tokenData);
	}

	sendInvoiceToGovernment(token: string, data: any): Observable<any> {
		const headers = new HttpHeaders({
			"Content-Type": "application/json",
			Authorization: `Bearer ${token}`,
		});

		const url =
			"https://openapi.taxes.gov.il/shaam/tsandbox/Invoices/v1/Approval";

		return this._HttpClient.post<any>(url, data, { headers: headers });
	}

	getValidToken(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}oauth/getValidToken`);
	}

	getAllTransactionsByMerchantId(id: any) {
		return this._HttpClient.get(
			`${baseUrl}zip/getAllTransactionsByMerchantId/${id}`
		);
	}

	/* start terms and conditions functions */

	getAllTermsAndConditions(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}terms-and-conditions`);
	}

	getAllActiveTermsAndConditions(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}terms-and-conditions/active`);
	}

	updateTermsData(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}terms-and-conditions/update/${id}`,
			model
		);
	}

	changeTermsStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}terms-and-conditions/update-status/${id}`,
			model
		);
	}

	deleteTermsById(id: number): Observable<any> {
		return this._HttpClient.delete<any>(
			`${baseUrl}terms-and-conditions/delete/${id}`
		);
	}

	getInvoiceAndRecipeToPdf(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/getInvoiceAndRecipeToPdf/${id}`
		);
	}

	/* start accounting office functions */

	getAllAccountingOffice(limit: number, offset: number): Observable<any> {
		const params = new HttpParams()
			.set("limit", limit.toString())
			.set("offset", offset.toString());

		return this._HttpClient.get<any>(
			`${baseUrl}accounting-office/getAllOffice`,
			{ params }
		);
	}

	changeAccountingOfficeStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}accounting-office/changeStatusOffice/${id}`,
			model
		);
	}

	updateOfficesById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}accounting-office/updateOffice/${id}`,
			model
		);
	}

	getALLActiveCompany(recordType: 'merchant' | 'accountant' | 'all' = 'merchant'): Observable<any> {
		const params = new HttpParams().set('recordType', recordType);
		return this._HttpClient.get<any>(`${baseUrl}company/getALLActiveCompany`, { params });
	}

	getAllPermissionsByOfficeId(
		id: number,
		limit: number,
		offset: number
	): Observable<any> {
		const params = new HttpParams()
			.set("limit", limit.toString())
			.set("offset", offset.toString());

		return this._HttpClient.get<any>(
			`${baseUrl}office-company-permissions/getAllPermissionsByOfficeId/${id}`,
			{ params }
		);
	}

	getAllUserAssesToCompany(
		officeId: number,
		companyId: number,
		limit?: number,
		offset?: number
	): Observable<any> {
		let params = new HttpParams()
			.set("officeId", officeId.toString())
			.set("companyId", companyId.toString());
		if (limit != null) {
			params = params.set("limit", String(limit));
		}
		if (offset != null) {
			params = params.set("offset", String(offset));
		}
		return this._HttpClient.get<any>(
			`${baseUrl}accountant-users-company-access/getAllUserAssesToCompany`,
			{ params }
		);
	}

	/** All access rows for a company (API returns `{ data, total }`). */
	getAllUserAssesToCompanyAll(
		officeId: number,
		companyId: number,
		pageSize = 500
	): Observable<any[]> {
		const load = (offset: number, acc: any[]): Observable<any[]> => {
			return this.getAllUserAssesToCompany(
				officeId,
				companyId,
				pageSize,
				offset
			).pipe(
				switchMap((res) => {
					if (Array.isArray(res)) {
						return of([...acc, ...res]);
					}
					const chunk = Array.isArray(res?.data) ? res.data : [];
					const total =
						typeof res?.total === "number" ? res.total : chunk.length;
					const next = [...acc, ...chunk];
					if (chunk.length === 0 || next.length >= total) {
						return of(next);
					}
					return load(offset + pageSize, next);
				})
			);
		};
		return load(0, []);
	}

	getAllPendingPermissionsByOffice(
		limit: number,
		offset: number
	): Observable<any> {
		const params = new HttpParams()
			.set("limit", limit.toString())
			.set("offset", offset.toString());

		return this._HttpClient.get<any>(
			`${baseUrl}office-company-permissions/getAllPendingPermissionsByOffice`,
			{ params }
		);
	}

	changeCompanyPermissionStatus(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}office-company-permissions/${id}`,
			model
		);
	}

	changeCompanyPermissionStatusAll(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}office-company-permissions/update-pending-status/${id}`,
			model
		);
	}

	getAllOfficeInfo(id: number): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}accounting-office/${id}`);
	}

	updateOfficeById(id: number, model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}accounting-office/updateOffice/${id}`,
			model
		);
	}

	getAllAgreedPermissionsByOfficeId(
		id: number,
		limit: number,
		offset: number,
		keyword?: string
	): Observable<any> {
		let params = new HttpParams()
			.set("limit", limit.toString())
			.set("offset", offset.toString());

		if (keyword) {
			params = params.set("keyword", keyword);
		}

		return this._HttpClient.get<any>(
			`${baseUrl}office-company-permissions/getAllAgreedPermissionsByOfficeId/${id}`,
			{ params }
		);
	}

	getAllAccessByUserId(
		id: number,
		limit: number,
		offset: number,
		keyword?: string
	): Observable<any> {
		let params = new HttpParams()
			.set("limit", limit.toString())
			.set("offset", offset.toString());

		if (keyword) {
			params = params.set("keyword", keyword);
		}
		return this._HttpClient.get<any>(
			`${baseUrl}accountant-users-company-access/getAllAccessByUserId/${id}`,
			{ params }
		);
	}

	getAllActiveAgreedPermissionsByOfficeId(
		id: number,
		limit?: number,
		offset?: number
	): Observable<any> {
		let params = new HttpParams();
		if (limit != null) {
			params = params.set("limit", String(limit));
		}
		if (offset != null) {
			params = params.set("offset", String(offset));
		}
		return this._HttpClient.get<any>(
			`${baseUrl}office-company-permissions/getAllActiveAgreedPermissionsByOfficeId/${id}`,
			{ params }
		);
	}

	/** All agreed permissions for an office (picker / multi-select; `{ data, total }`). */
	getAllActiveAgreedPermissionsByOfficeIdAll(
		officeId: number,
		pageSize = 500
	): Observable<any[]> {
		const load = (offset: number, acc: any[]): Observable<any[]> => {
			return this.getAllActiveAgreedPermissionsByOfficeId(
				officeId,
				pageSize,
				offset
			).pipe(
				switchMap((res) => {
					if (Array.isArray(res)) {
						return of([...acc, ...res]);
					}
					const chunk = Array.isArray(res?.data) ? res.data : [];
					const total =
						typeof res?.total === "number" ? res.total : chunk.length;
					const next = [...acc, ...chunk];
					if (chunk.length === 0 || next.length >= total) {
						return of(next);
					}
					return load(offset + pageSize, next);
				})
			);
		};
		return load(0, []);
	}

	deleteOnAccess(id: number): Observable<any> {
		return this._HttpClient.delete<any>(
			`${baseUrl}accountant-users-company-access/deleteOnAccess/${id}`
		);
	}

	deleteRelatedOfficeToCompany(id: number): Observable<any> {
		return this._HttpClient.delete<any>(
			`${baseUrl}office-company-permissions/deleteRelatedOfficeToCompany/${id}`
		);
	}

	getAllPermissionsByMerchantId(
		id: number,
		limit?: number,
		offset?: number
	): Observable<any> {
		let params = new HttpParams();
		if (limit != null) {
			params = params.set("limit", String(limit));
		}
		if (offset != null) {
			params = params.set("offset", String(offset));
		}
		return this._HttpClient.get<any>(
			`${baseUrl}office-company-permissions/getAllPermissionsByMerchantId/${id}`,
			{ params }
		);
	}

	/** Loads every permission row for the merchant (API returns `{ data, total }`). */
	getAllPermissionsByMerchantIdAll(
		merchantId: number,
		pageSize = 500
	): Observable<any[]> {
		const load = (offset: number, acc: any[]): Observable<any[]> => {
			return this.getAllPermissionsByMerchantId(
				merchantId,
				pageSize,
				offset
			).pipe(
				switchMap((res) => {
					if (Array.isArray(res)) {
						return of([...acc, ...res]);
					}
					const chunk = Array.isArray(res?.data) ? res.data : [];
					const total =
						typeof res?.total === "number" ? res.total : chunk.length;
					const next = [...acc, ...chunk];
					if (chunk.length === 0 || next.length >= total) {
						return of(next);
					}
					return load(offset + pageSize, next);
				})
			);
		};
		return load(0, []);
	}

	getAllActiveAccountingOffices(
		limit?: number,
		offset?: number
	): Observable<any> {
		let params = new HttpParams();
		if (limit != null) {
			params = params.set("limit", String(limit));
		}
		if (offset != null) {
			params = params.set("offset", String(offset));
		}
		return this._HttpClient.get<any>(
			`${baseUrl}accounting-office/getAllActiveAccountingOffices`,
			{ params }
		);
	}

	/** Loads every page for office pickers (API returns `{ data, total }`). */
	getAllActiveAccountingOfficesAll(
		pageSize = 500
	): Observable<any[]> {
		const load = (offset: number, acc: any[]): Observable<any[]> => {
			return this.getAllActiveAccountingOffices(pageSize, offset).pipe(
				switchMap((res) => {
					if (Array.isArray(res)) {
						return of([...acc, ...res]);
					}
					const chunk = Array.isArray(res?.data) ? res.data : [];
					const total =
						typeof res?.total === "number" ? res.total : chunk.length;
					const next = [...acc, ...chunk];
					if (chunk.length === 0 || next.length >= total) {
						return of(next);
					}
					return load(offset + pageSize, next);
				})
			);
		};
		return load(0, []);
	}

	getAllActiveCompanyTypes(): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}company-type/active`
		);
	}

	getAllActiveCompanyByMerchant(): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}company/getAllActiveCompanyByMerchant`
		);
	}

	getAllCompanyDocumentsComponent(
		id: number,
		limit: number,
		offset: number,
		fromDate: any,
		toDate: any,
		searchKeyword: any
	): Observable<any> {
		let params = new HttpParams()
			.set("limit", limit.toString())
			.set("offset", offset.toString());

		if (fromDate) {
			params = params.set("fromDate", fromDate.toString());
		}

		if (toDate) {
			params = params.set("toDate", toDate.toString());
		}

		if (searchKeyword) {
			params = params.set("searchKeyword", searchKeyword.toString());
		}

		return this._HttpClient.get<any>(
			`${baseUrl}invoice/allInvoiceByCompanyId/${id}`,
			{ params }
		);
	}

	printAllInvoiceByCompanyId(
		id: number,
		fromDate: any,
		toDate: any
	): Observable<any> {
		const params = new HttpParams()
			.set("fromDate", fromDate?.toString())
			.set("toDate", toDate?.toString());
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/printAllInvoiceByCompanyId/${id}`,
			{ params }
		);
	}

	/* start reports functions */

	getAllReportsIncome(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);

		return this._HttpClient.get<any>(`${baseUrl}reports/income`, { params });
	}

	/**
	 * Get financial report (gain/loss) JSON data
	 * GET reports/{companyId}/financial-report?startDate=&endDate=
	 */
	getFinancialReportJson(
		companyId: string | number,
		startDate: string,
		endDate: string
	): Observable<{
		incomeSummary: {
			taxable_income: number | null;
			non_taxable_income: number | null;
			vat_amount: number | null;
			total_income_including_vat: number | null;
		};
		invoiceDetails: any[];
	}> {
		const params = new HttpParams()
			.set("startDate", startDate)
			.set("endDate", endDate);
		return this._HttpClient.get<any>(
			`${baseUrl}reports/${companyId}/financial-report`,
			{ params }
		);
	}

	getProfitLossReport(
		companyId: string | number,
		startDate: string,
		endDate: string,
		confirmationStatus: 'all' | 'confirmed' | 'unconfirmed' = 'all',
		dateType: 'upload_date' | 'document_date' = 'document_date'
	): Observable<any> {
		const params = new HttpParams()
			.set('company_id', String(companyId))
			.set('start_date', startDate)
			.set('end_date', endDate)
			.set('confirmation_status', confirmationStatus)
			.set('date_type', dateType);
		return this._HttpClient.get<any>(`${baseUrl}expenses/reports`, { params });
	}

	getProfitLossReportPdf(
		companyId: string | number,
		startDate: string,
		endDate: string,
		lang: string,
		confirmationStatus: 'all' | 'confirmed' | 'unconfirmed' = 'all',
		dateType: 'upload_date' | 'document_date' = 'document_date'
	): Observable<Blob> {
		const params = new HttpParams()
			.set('company_id', String(companyId))
			.set('start_date', startDate)
			.set('end_date', endDate)
			.set('confirmation_status', confirmationStatus)
			.set('date_type', dateType)
			.set('lang', lang);
		return this._HttpClient.get<Blob>(`${baseUrl}expenses/reports/pdf`, {
			params,
			responseType: 'blob' as 'json',
		});
	}

	getFinanicalReport(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			// .set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);

		return this._HttpClient.get<Blob>(
			`${baseUrl}reports/${companyId}/financial-report/pdf`,
			{ params, responseType: "blob" as "json" }
		);
	}

	getAllReportsIncomeExel(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<Blob> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);

		return this._HttpClient.get<Blob>(`${baseUrl}reports/income/excel`, {
			params,
			responseType: "blob" as "json",
		});
	}

	getAllReportsIncomePdf(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<Blob> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);

		return this._HttpClient.get<Blob>(`${baseUrl}reports/income/pdf`, {
			params,
			responseType: "blob" as "json",
		});
	}

	getAllConsoliDatedIncome(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<any>(`${baseUrl}reports/consolidated-income`, {
			params,
		});
	}

	getAllConsoliDatedIncomeExel(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<Blob> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<Blob>(
			`${baseUrl}reports/consolidated-income/excel`,
			{ params, responseType: "blob" as "json" }
		);
	}

	getAllConsoliDatedIncomePdf(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<Blob> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<Blob>(
			`${baseUrl}reports/consolidated-income-report-pdf`,
			{ params, responseType: "blob" as "json" }
		);
	}

	getAllReportsDocument(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<any>(`${baseUrl}reports/document`, { params });
	}

	getAllReportsDocumentExel(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<any>(`${baseUrl}reports/document/excel`, {
			params,
			responseType: "blob" as "json",
		});
	}

	getAllReportsDocumentPdf(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<any>(`${baseUrl}reports/document/pdf`, {
			params,
			responseType: "blob" as "json",
		});
	}

	getAllReportsItem(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<any>(`${baseUrl}reports/item`, { params });
	}

	getAllReportsItemExel(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<any>(`${baseUrl}reports/item/excel`, {
			params,
			responseType: "blob" as "json",
		});
	}

	getAllReportsItemPdf(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<any>(`${baseUrl}reports/item-pdf`, {
			params,
			responseType: "blob" as "json",
		});
	}

	getAllReportsConsolidatedIitem(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<any>(`${baseUrl}reports/consolidated-item`, {
			params,
		});
	}

	getAllConsolidatedItemPdf(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<Blob> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);

		return this._HttpClient.get<Blob>(
			`${baseUrl}reports/consolidated-item-pdf`,
			{ params, responseType: "blob" as "json" }
		);
	}

	getAllReportsConsolidatedIitemeExel(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<any>(
			`${baseUrl}reports/consolidated-item/excel`,
			{ params, responseType: "blob" as "json" }
		);
	}

	getAllReceiptsDetails(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<any>(`${baseUrl}reports/receipts-details`, {
			params,
		});
	}

	getAllReceiptsDetailsExel(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<any>(
			`${baseUrl}reports/receipts-details/excel`,
			{ params, responseType: "blob" as "json" }
		);
	}

	getAllReceiptsDetailsPdf(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<any>(`${baseUrl}reports/receipts-details-pdf`, {
			params,
			responseType: "blob" as "json",
		});
	}

	getDashboardReport(
		companyId: string,
		startDate: string,
		endDate: string,
		lang: string
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", lang);
		return this._HttpClient.get<any>(`${baseUrl}reports/getDashboardReport`, {
			params,
		});
	}

	getDashboardReportExel(companyId: string, lang: string): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)

			.set("lang", lang);
		return this._HttpClient.get<any>(
			`${baseUrl}reports/getDashboardReport/excel`,
			{ params, responseType: "blob" as "json" }
		);
	}

	getDashboardReportPdf(companyId: string, lang: string): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)

			.set("lang", lang);
		return this._HttpClient.get<any>(
			`${baseUrl}reports/getDashboardReport-pdf`,
			{ params, responseType: "blob" as "json" }
		);
	}

	getDashboardReports(companyId: string): Observable<any> {
		const params = new HttpParams().set("companyId", companyId);
		// .set('startDate', startDate)
		// .set('endDate', endDate)
		// .set('lang', lang)
		return this._HttpClient.get<any>(`${baseUrl}reports/getDashboardReport`, {
			params,
		});
	}

	getSummaryReport(companyId: string): Observable<any> {
		const params = new HttpParams().set("companyId", companyId);
		// .set('startDate', startDate)
		// .set('endDate', endDate)
		// .set('lang', lang)
		return this._HttpClient.get<any>(`${baseUrl}reports/getSummaryReport`, {
			params,
		});
	}

	getSummaryReportExel(companyId: string, lang: string): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)

			.set("lang", lang);
		return this._HttpClient.get<any>(
			`${baseUrl}reports/getSummaryReport/excel`,
			{ params, responseType: "blob" as "json" }
		);
	}

	getSummaryReportpdf(companyId: string, lang: string): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)

			.set("lang", lang);
		return this._HttpClient.get<any>(`${baseUrl}reports/getSummaryReport-pdf`, {
			params,
			responseType: "blob" as "json",
		});
	}

	getSummaryReportHtml(companyId: string, lang: string): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)

			.set("lang", lang);
		return this._HttpClient.get<any>(`${baseUrl}reports/getSummaryReportHtml`, {
			params,
			responseType: "text" as "json",
		});
	}

	getIncomeReportHtml(
		companyId: string,
		lang: string,
		startDate: any,
		endDate: any
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)

			.set("lang", lang)
			.set("startDate", startDate)
			.set("endDate", endDate);
		return this._HttpClient.get<any>(`${baseUrl}reports/income/html`, {
			params,
			responseType: "text" as "json",
		});
	}

	getIncomeReport(
		companyId: string,
		startDate: any,
		endDate: any
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("startDate", startDate)
			.set("endDate", endDate);
		return this._HttpClient.get<any>(`${baseUrl}reports/income`, { params });
	}

	checkSequenceExists(companyId: number, docType: string): Observable<boolean> {
		return this._HttpClient.get<boolean>( `${baseUrl}company/${companyId.toString()}/sequence/${docType}` );
	}

	createSequence(
		companyId: number,
		docType: string,
		startWith: number
	): Observable<void> {
		return this._HttpClient.post<void>(`${baseUrl}company/createSequence`, {
			companyId,
			docType,
			startWith,
		});
	}

	/* */

	getOfficeStatistics(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}office-company-permissions/getOfficeStatistics/${id}`
		);
	}

	setCompanyDefault(model: any): Observable<any> {
		return this._HttpClient.put<any>(
			`${baseUrl}users/setCompanyDefault`,
			model
		);
	}

	// ConectWithIsr(companyId: any): Observable<any> {
	//   const params = new HttpParams().set('companyId', companyId);
	//   const url = environment.production ? environment.apiUrl : `${environment.apiUrl}`;

	//   return this._HttpClient.get<any>(url, { params });
	// }

	//ConnectWithTaxGovernment(companyId: any): Observable<any> {
	//const params = new HttpParams().set('companyId', companyId);

	//return this._HttpClient.get<any>(`https://intg.thinvoices.com/oauth-prod/authorization-code`, { params });
	//}

	ConnectWithTaxGovernment(companyId: any): Observable<any> {
		const params = new HttpParams().set("companyId", companyId);
		return this._HttpClient.get<any>(`${baseUrl}ita-oauth/authorization-url`, { params });
	}

	uploadExcelFile(formData: FormData): Observable<any> {
		return this._HttpClient.post<any>(`${baseUrl}zip/uploadExcel`, formData);
	}

	deleteConfirmationNumberByUuid(invoice_uuid: string): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/deleteConfirmationNumberByUuid/${invoice_uuid}`
		);
	}

	getAllTransactionsHistoryZipFile(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}zip/getAllTransactions`);
	}

	getAllResponseLogsByLogId(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}zip/getAllResponseLogsByLogId/${id}`
		);
	}

	/* start reports functions */

	getAllClosedMonthCompany(
		companyId: number,
		officeId: number
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("officeId", officeId);
		return this._HttpClient.get<any>(`${baseUrl}closed-months/company-office`, {
			params,
		});
	}

	changeLockClosedStatus(id: any, updatedBy: any): Observable<any> {
		const params = new HttpParams().set("updatedBy", updatedBy);
		return this._HttpClient.put<any>(
			`${baseUrl}closed-months/${id}/toggle-lock`,
			null,
			{ params }
		);
	}

	getLockedMonthInvoicesByAccess(): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}accounting-office/getLockedMonthInvoicesByAccess`
		);
	}

	cancleAert(invoice_id: any, updated_by: any): Observable<any> {
		const params = new HttpParams().set("updated_by", updated_by);
		return this._HttpClient.put<any>(
			`${baseUrl}invoice/cancel-alert/${invoice_id}`,
			null,
			{ params }
		);
	}

	checkClosedMonth(
		companyId: number,
		month: number,
		year: number
	): Observable<any> {
		const params = new HttpParams()
			.set("companyId", companyId)
			.set("month", month.toString())
			.set("year", year.toString());

		return this._HttpClient.get<any>(`${baseUrl}closed-months/check`, {
			params,
		});
	}

	deleteClosedMonth(id: number): Observable<any> {
		return this._HttpClient.delete<any>(`${baseUrl}closed-months/${id}`);
	}

	getAllInvoiceLockedByCompanyId(companyId: number): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}invoice/locked/${companyId}`);
	}

	// integration

	InvoiceDecisionApiCancel(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}integration/InvoiceDecisionApiCancel/${id}`
		);
	}

	InvoiceDecisionApiContinue(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}integration/InvoiceDecisionApiContinue/${id}`
		);
	}

	InvoiceDecisionApiFurtherObjection(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}integration/InvoiceDecisionApiFurtherObjection/${id}`
		);
	}

	InvoiceAfterHearingApproval(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}integration/InvoiceAfterHearingApproval/${id}`
		);
	}

	InvoiceReversingTheCharge(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}integration/InvoiceReversingTheCharge/${id}`
		);
	}

	reSendInvoiceSecondToGovernmentMainProject(id: number): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}integration/sendInvoiceSecondToGovernmentMainProject/${id}`
		);
	}

	getMerchantDocuments(page: number, size: number): Observable<any> {
		let params = new HttpParams().set("limit", size).set("offset", page * size);
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/getALLInvoiceByBasedMerchantId`,
			{ params }
		);
	}

	/*getMerchantDocuments(page: number, size: number): Observable<any> {
		let params = new HttpParams().set("limit", size).set("offset", page * size);
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/getALLInvoiceByBasedMerchantId`,
			{ params }
		);
	}
*/
getFilteredDocuments(filters: any): Observable<any> {
	const {
		fromDate,
		toDate,
		companyId,
		invoice_type,
		customer_id,
		merchant_id,
		searchKeyword,
		status,
		limit,
		offset,
		sortIndex,
		sort
	} = filters;

	let params = new HttpParams()
		.set("fromDate", fromDate || "")
		.set("toDate", toDate || "")
		.set("companyId", companyId || "")
		.set("invoice_type", invoice_type || "")
		.set("merchant_id", merchant_id || "")
		.set("searchKeyword", searchKeyword || "")
		.set("customer_id", customer_id || "")
		.set("limit", limit)
		.set("offset", offset)
		.set("sortIndex", sortIndex !== undefined && sortIndex !== null ? sortIndex.toString() : "")
		.set("sort", sort);

	if (status !== null) {
		params = params.set("status", status.toString());
	}

	return this._HttpClient.get<any>(`${baseUrl}invoice/filter`, { params });
}
	
	createRefNumber(invoice_uuid: string): Observable<any> {
		return this._HttpClient.get<any>(
			`${baseUrl}integration/sendInvoiceSecondToGovernmentMainProject/${invoice_uuid}`
		);
	}

	exportReportToPdf(filters: any): Observable<Blob> {
		const {
			fromDate,
			toDate,
			companyId,
			invoice_type,
			customer_id,
			searchKeyword,
			status,
			limit,
			offset,
			sortIndex,
			sort,
			lang
		} = filters;
		
		let params = new HttpParams()
			.set("fromDate", fromDate || "")
			.set("toDate", toDate || "")
			.set("companyId", companyId || "")
			.set("lang", lang || "en")
			 .set("limit", limit || 10000) // Use a large number to get all records for PDF
			.set("offset", offset || 0)
			.set("invoice_type", invoice_type || "")
			.set("searchKeyword", searchKeyword || "")
			.set("sort", sort || "DESC");

		// Handle sortIndex safely - convert to string only if defined
		if (sortIndex !== undefined && sortIndex !== null) {
			params = params.set("sortIndex", sortIndex.toString());
		} else {
			params = params.set("sortIndex", "0");
		}

		// Handle customer_id safely
		if (customer_id) {
			params = params.set("customer_id", customer_id);
		}
		
		// Handle status separately to avoid sending null or undefined
		if (status !== null && status !== undefined) {
			params = params.set("status", status.toString());
		}

		return this._HttpClient.get(`${baseUrl}documents/export/report`, {
			params,
			responseType: 'blob'
		});
	}

	downloadAccountStatement(customerId: number, filters: any): Observable<Blob> {
		// filters.startDate and filters.endDate should already be in YYYY-MM-DD format from the component
		const startDate = filters.startDate;
		const endDate = filters.endDate;

		if (!startDate || !endDate) {
			console.error("Invalid startDate or endDate:", { startDate, endDate, filters });
			return throwError(() => new Error("Both startDate and endDate must be valid."));
		}

		// Validate date format (should be YYYY-MM-DD)
		const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
		if (!dateRegex.test(startDate) || !dateRegex.test(endDate)) {
			console.error("Invalid date format. Expected YYYY-MM-DD, got:", { startDate, endDate });
			return throwError(() => new Error(`Invalid date format. Expected YYYY-MM-DD, got startDate: ${startDate}, endDate: ${endDate}`));
		}

		// Get companyId from filters - it's required for the new endpoint
		const companyId = filters.companyId;
		
		if (!companyId) {
			console.error("companyId is required for the new endpoint");
			return throwError(() => new Error("companyId is required"));
		}

		let params = new HttpParams()
			.set("startDate", startDate)
			.set("endDate", endDate)
			.set("lang", filters.lang || "en"); // Add language parameter with default fallback

		// Add claim parameter: false = حسب الفاتورة الضريبية, true = حسب المطالبة المالية
		// Convert to boolean string for the API
		if (filters.claim !== undefined && filters.claim !== null) {
			const claimValue = filters.claim === true || filters.claim === 1 || filters.claim === '1' || filters.claim === 'true' ? 'true' : 'false';
			params = params.set("claim", claimValue);
		}

		// New endpoint format: invoice-reports/customer-account-statement-pdf/{customerId}/{companyId}
		const url = `${baseUrl}invoice-reports/customer-account-statement-pdf/${customerId}/${companyId}`;
		return this._HttpClient.get(url, {
			params,
			responseType: 'blob'
		});
	}

	// Format date to YYYY-MM-DD
	private formatDate(date: string | Date): string {
		if (!date) {
			return "";
		}
		const parsedDate = new Date(date);
		if (isNaN(parsedDate.getTime())) {
			return "";
		}
		const year = parsedDate.getFullYear();
		const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
		const day = String(parsedDate.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	/**
	 * Get account statement summary for a customer (for PDF generation)
	 * @param customerId Customer ID
	 * @param startDate Start date (YYYY-MM-DD or Date)
	 * @param endDate End date (YYYY-MM-DD or Date)
	 */
	getAccountStatementSummary(customerId: number, startDate: string | Date, endDate: string | Date): Observable<any> {
		const formattedStartDate = this.formatDate(startDate);
		const formattedEndDate = this.formatDate(endDate);
		if (!formattedStartDate || !formattedEndDate) {
			return throwError(() => new Error('Both startDate and endDate must be valid.'));
		}
		const params = new HttpParams()
			.set('startDate', formattedStartDate)
			.set('endDate', formattedEndDate);
		return this._HttpClient.get<any>(
			`${baseUrl}invoice/account-statement/${customerId}/summary`,
			{ params }
		);
	}

	/* start invoice-reports functions */

	/**
	 * Get company customers account summary
	 * @param companyId Company ID
	 * @param startDate Start date (YYYY-MM-DD)
	 * @param endDate End date (YYYY-MM-DD)
	 * @param lang Language (ar, en, he)
	 * @param claim Account type: false = tax invoice, true = financial claim
	 */
	getCompanyCustomersAccountSummary(companyId: number, startDate: string, endDate: string, lang: string = 'ar', claim?: boolean): Observable<any> {
		let params = new HttpParams()
			.set('startDate', startDate)
			.set('endDate', endDate)
			.set('lang', lang);
		
		// Add claim parameter if provided
		if (claim !== undefined && claim !== null) {
			params = params.set('claim', claim.toString());
		}
		
		return this._HttpClient.get<any>(
			`${baseUrl}invoice-reports/company-customers-account-summary/${companyId}`,
			{ params }
		);
	}

	/**
	 * Get company customers detailed account statement PDF
	 * @param companyId Company ID
	 * @param startDate Start date (YYYY-MM-DD)
	 * @param endDate End date (YYYY-MM-DD)
	 * @param lang Language (ar, en, he)
	 * @param claim Account type: false = tax invoice, true = financial claim
	 */
	getCompanyCustomersAccountStatementPdf(companyId: number, startDate: string, endDate: string, lang: string = 'ar', claim?: boolean): Observable<Blob> {
		let params = new HttpParams()
			.set('startDate', startDate)
			.set('endDate', endDate)
			.set('lang', lang);
		
		// Add claim parameter if provided
		if (claim !== undefined && claim !== null) {
			params = params.set('claim', claim.toString());
		}
		
		return this._HttpClient.get(
			`${baseUrl}invoice-reports/company-customers-detailed-account-statement-pdf/${companyId}`,
			{ params, responseType: 'blob' }
		);
	}

	/**
	 * Get customer account summary for a specific customer
	 * @param customerId Customer ID
	 * @param companyId Company ID
	 * @param startDate Start date (YYYY-MM-DD)
	 * @param endDate End date (YYYY-MM-DD)
	 * @param lang Language (ar, en, he)
	 */
	getCustomerAccountSummary(
		customerId: number,
		companyId: number,
		startDate: string,
		endDate: string,
		lang: string = 'ar',
		claim?: boolean,
		documentTypes?: string,
		search?: string,
	): Observable<any> {
		let params = new HttpParams()
			.set('startDate', startDate)
			.set('endDate', endDate)
			.set('lang', lang);
		if (claim !== undefined && claim !== null) {
			params = params.set('claim', claim.toString());
		}
		if (documentTypes?.trim()) {
			params = params.set('documentTypes', documentTypes.trim());
		}
		if (search?.trim()) {
			params = params.set('search', search.trim());
		}
		return this._HttpClient.get<any>(
			`${baseUrl}invoice-reports/customer-account-summary/${customerId}/${companyId}`,
			{ params }
		);
	}

	/**
	 * Get customer account statement PDF for a specific customer
	 * @param customerId Customer ID
	 * @param companyId Company ID
	 * @param startDate Start date (YYYY-MM-DD)
	 * @param endDate End date (YYYY-MM-DD)
	 * @param lang Language (ar, en, he)
	 * @param claim Account type: false = tax invoice, true = financial claim
	 */
	getCustomerAccountStatementPdf(
		customerId: number,
		companyId: number,
		startDate: string,
		endDate: string,
		lang: string = 'ar',
		claim?: boolean,
		documentTypes?: string,
		search?: string,
	): Observable<Blob> {
		let params = new HttpParams()
			.set('startDate', startDate)
			.set('endDate', endDate)
			.set('lang', lang);
		
		// Add claim parameter if provided: false = حسب الفاتورة الضريبية, true = حسب المطالبة المالية
		if (claim !== undefined && claim !== null) {
			const claimValue = claim === true ? 'true' : 'false';
			params = params.set('claim', claimValue);
		}
		if (documentTypes?.trim()) {
			params = params.set('documentTypes', documentTypes.trim());
		}
		if (search?.trim()) {
			params = params.set('search', search.trim());
		}
		
		return this._HttpClient.get(
			`${baseUrl}invoice-reports/customer-account-statement-pdf/${customerId}/${companyId}`,
			{ params, responseType: 'blob' }
		);
	}

	getCustomerAccountStatementExcel(
		customerId: number,
		companyId: number,
		startDate: string,
		endDate: string,
		lang: string = 'ar',
		claim?: boolean,
		documentTypes?: string,
		search?: string,
	): Observable<Blob> {
		let params = new HttpParams()
			.set('startDate', startDate)
			.set('endDate', endDate)
			.set('lang', lang);
		if (claim !== undefined && claim !== null) {
			params = params.set('claim', claim === true ? 'true' : 'false');
		}
		if (documentTypes?.trim()) {
			params = params.set('documentTypes', documentTypes.trim());
		}
		if (search?.trim()) {
			params = params.set('search', search.trim());
		}
		return this._HttpClient.get(
			`${baseUrl}invoice-reports/customer-account-statement-excel/${customerId}/${companyId}`,
			{ params, responseType: 'blob' },
		);
	}

	/* end invoice-reports functions */

	/**
	 * Send invoice to customer via email
	 * @param invoiceId Invoice ID
	 * @param tempEmail Optional temporary email for one-time use (if provided, customer won't be updated)
	 */
	sendInvoiceToCustomerEmail(invoiceId: number, tempEmail?: string): Observable<any> {
		const payload = tempEmail ? { tempEmail: tempEmail } : {};
		return this._HttpClient.post<any>(`${baseUrl}email/send-invoice/${invoiceId}`, payload);
	}

	/* ─── Admin App Version ─── */

	getAdminAppVersion(): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}admin/app-versions`);
	}

	upsertAdminAppVersion(body: {
		latest_version: string;
		minimum_required_version: string;
		update_message_en?: string;
		update_message_ar?: string;
		update_message_he?: string;
		google_play_url?: string;
		app_store_url?: string;
		is_active?: boolean;
	}): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}admin/app-versions`, body);
	}

	deactivateAdminAppVersion(id: number): Observable<any> {
		return this._HttpClient.delete<any>(`${baseUrl}admin/app-versions/${id}`);
	}

	getAdminAppVersionHistory(): Observable<any[]> {
		return this._HttpClient.get<any[]>(`${baseUrl}admin/app-versions/history`);
	}

	// ── User notification inbox ──────────────────────────────────────────────

	getMyNotifications(
		page: number,
		limit: number,
		filters: NotificationInboxFilters = {},
	): Observable<{ data: UserNotificationItem[]; total: number; unread: number }> {
		let params = new HttpParams()
			.set('page', String(page))
			.set('limit', String(limit));

		if (filters.isRead === true) {
			params = params.set('is_read', 'true');
		} else if (filters.isRead === false) {
			params = params.set('is_read', 'false');
		}
		if (filters.notificationType) {
			params = params.set('notification_type', filters.notificationType);
		}
		if (filters.notificationSource) {
			params = params.set('notification_source', filters.notificationSource);
		}

		return this._HttpClient.get<{ data: UserNotificationItem[]; total: number; unread: number }>(
			`${baseUrl}notifications/my`,
			{ params },
		);
	}

	getMyUnreadNotificationCount(filters: NotificationInboxFilters = {}): Observable<{ count: number }> {
		let params = new HttpParams();
		if (filters.notificationType) {
			params = params.set('notification_type', filters.notificationType);
		}
		if (filters.notificationSource) {
			params = params.set('notification_source', filters.notificationSource);
		}
		return this._HttpClient.get<{ count: number }>(
			`${baseUrl}notifications/my/unread-count`,
			{ params },
		);
	}

	markAllNotificationsRead(): Observable<{ success: boolean }> {
		return this._HttpClient.put<{ success: boolean }>(`${baseUrl}notifications/my/mark-all-read`, {});
	}

	markNotificationRead(id: number): Observable<{ success: boolean }> {
		return this._HttpClient.put<{ success: boolean }>(`${baseUrl}notifications/my/${id}/read`, {});
	}

	/* customer account entries */

	createCustomerAccountEntry(body: {
		company_id: number;
		customer_id: number;
		entry_date: string;
		amount: number;
		entry_type: 'debit' | 'credit';
		currency?: string;
		note?: string;
	}): Observable<any> {
		return this._HttpClient.post<any>(`${baseUrl}customer-account-entries`, body);
	}

	getCustomerAccountEntries(params: {
		company_id: number;
		customer_id?: number;
		startDate?: string;
		endDate?: string;
	}): Observable<any> {
		let httpParams = new HttpParams().set('company_id', params.company_id.toString());
		if (params.customer_id != null) {
			httpParams = httpParams.set('customer_id', params.customer_id.toString());
		}
		if (params.startDate) {
			httpParams = httpParams.set('startDate', params.startDate);
		}
		if (params.endDate) {
			httpParams = httpParams.set('endDate', params.endDate);
		}
		return this._HttpClient.get<any>(`${baseUrl}customer-account-entries`, { params: httpParams });
	}

	getCustomerAccountEntryById(id: number): Observable<any> {
		return this._HttpClient.get<any>(`${baseUrl}customer-account-entries/${id}`);
	}

	updateCustomerAccountEntry(
		id: number,
		body: Partial<{
			entry_date: string;
			amount: number;
			entry_type: 'debit' | 'credit';
			currency: string;
			note: string;
		}>
	): Observable<any> {
		return this._HttpClient.put<any>(`${baseUrl}customer-account-entries/${id}`, body);
	}

	deleteCustomerAccountEntry(id: number): Observable<any> {
		return this._HttpClient.delete<any>(`${baseUrl}customer-account-entries/${id}`);
	}
}
